import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';

import {
    apiFetch, setAccessToken as apiSetAccess, getAccessToken,
    registerRefreshHandler, registerSignedOutHandler, ApiError,
} from './api';
import type { LoginResponse, User } from './types';

// ----------------------------------------------------------------------
// AuthContext
// ----------------------------------------------------------------------
// The access token lives in memory only (XSS-resilient — never in localStorage).
// The refresh token is the httpOnly `eit_rt` cookie set by the backend, so the
// browser carries it on every fetch without app code touching it.
//
// On first mount we try a silent /api/auth/refresh; if that succeeds the user
// arrives at the site already signed in (common when revisiting from a closed
// tab). If it fails we render the unauthenticated routes.

interface AuthState {
    user: User | null;
    accessToken: string | null;
    status: 'unknown' | 'guest' | 'authed';
}

interface AuthApi extends AuthState {
    signIn:   (email: string, password: string) => Promise<User>;
    signOut:  () => Promise<void>;
    activate: (token: string, password: string) => Promise<User>;
    refresh:  () => Promise<string | null>;
    /** Mutate the user in context after a profile edit, language toggle, etc. */
    patchUser: (p: Partial<User>) => void;
}

const Ctx = createContext<AuthApi | null>(null);

export function useAuth(): AuthApi {
    const v = useContext(Ctx);
    if (!v) throw new Error('useAuth must be used within <AuthProvider>');
    return v;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({ user: null, accessToken: null, status: 'unknown' });
    const refreshInFlight = useRef<Promise<string | null> | null>(null);

    const applyAuth = useCallback((user: User, accessToken: string) => {
        apiSetAccess(accessToken);
        setState({ user, accessToken, status: 'authed' });
    }, []);

    const clearAuth = useCallback(() => {
        apiSetAccess(null);
        setState({ user: null, accessToken: null, status: 'guest' });
    }, []);

    // Silent refresh — used by the API fetch wrapper on 401, and once at boot.
    const refresh = useCallback<AuthApi['refresh']>(async () => {
        if (refreshInFlight.current) return refreshInFlight.current;
        const p = (async () => {
            try {
                const r = await apiFetch<LoginResponse>('/api/auth/refresh', {
                    method: 'POST', noAuth: true, noRefreshRetry: true,
                });
                applyAuth(r.user, r.accessToken);
                return r.accessToken;
            } catch {
                clearAuth();
                return null;
            }
        })();
        refreshInFlight.current = p;
        try { return await p; } finally { refreshInFlight.current = null; }
    }, [applyAuth, clearAuth]);

    const signIn = useCallback<AuthApi['signIn']>(async (email, password) => {
        const r = await apiFetch<LoginResponse>('/api/auth/login', {
            method: 'POST', noAuth: true,
            body: { email, password },
        });
        applyAuth(r.user, r.accessToken);
        return r.user;
    }, [applyAuth]);

    const activate = useCallback<AuthApi['activate']>(async (token, password) => {
        const r = await apiFetch<LoginResponse>('/api/auth/activate', {
            method: 'POST', noAuth: true,
            body: { token, password },
        });
        applyAuth(r.user, r.accessToken);
        return r.user;
    }, [applyAuth]);

    const signOut = useCallback<AuthApi['signOut']>(async () => {
        try { await apiFetch('/api/auth/logout', { method: 'POST', noAuth: true, noRefreshRetry: true }); }
        catch { /* best-effort */ }
        clearAuth();
    }, [clearAuth]);

    const patchUser = useCallback<AuthApi['patchUser']>((p) => {
        setState((s) => (s.user ? { ...s, user: { ...s.user, ...p } } : s));
    }, []);

    // Wire the fetch wrapper to this auth instance.
    useEffect(() => {
        registerRefreshHandler(refresh);
        registerSignedOutHandler(() => {
            // Only clear when we KNOW the refresh path is exhausted; the API
            // layer calls this after a final 401, so it's safe to flush state.
            if (getAccessToken() === null) clearAuth();
        });
        return () => {
            registerRefreshHandler(null);
            registerSignedOutHandler(null);
        };
    }, [refresh, clearAuth]);

    // Boot — try a silent refresh once.
    useEffect(() => {
        if (state.status === 'unknown') {
            void refresh().then((tok) => {
                if (!tok) setState((s) => ({ ...s, status: 'guest' }));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const api = useMemo<AuthApi>(() => ({
        ...state, signIn, signOut, activate, refresh, patchUser,
    }), [state, signIn, signOut, activate, refresh, patchUser]);

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export { ApiError };
